/**
 * A refusal, read and answered: what the door said (in its own words), where
 * its shield falls, and the fix-it cards it offers, keyed by `refusal.by` and
 * the call that met it. Fix-its are ordinary calls with their arguments bound;
 * dropping one runs it through the same door. There is no "outrank" remedy:
 * a rank sent in a claim is trusted by the API, and the playground will not
 * teach anyone to lean on that. Pure.
 */

import type { Args, CallRecord, Cue, FixIt, Json, WorldMatter } from '../contracts.ts';
import { worldKey } from '../worlds.ts';
import { maskOf, nextFreeRun, readSlots, runsOf, slotsOf } from './masks.ts';
import { positionOf, samePosition } from './relay-geom.ts';
import { objectOf } from './json.ts';

export interface Refusal {
  readonly by: string;
  readonly detail: string;
  readonly slots: bigint;
  readonly conflictsWith: readonly string[];
  /** The refusal object as the server sent it. */
  readonly fields: Readonly<Record<string, Json>>;
}

const strings = (v: Json | undefined): string[] => (Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : []);

/** The refusal in a 409 problem body, or null when the answer is not one. */
export const refusalOf = (body: Json | undefined): Refusal | null => {
  const problem = objectOf(body);
  const r = objectOf(problem.refusal);
  if (typeof r.by !== 'string') return null;
  return {
    by: r.by,
    detail: typeof problem.detail === 'string' ? problem.detail : '',
    slots: maskOf(r.slots),
    conflictsWith: strings(r.conflictsWith ?? r.holding),
    fields: r,
  };
};

const bodyOf = (args: Args): Record<string, Json> => objectOf(args.body);

/** The position part of a body (`date`, `hour`, or the raw form), to bind into another call. */
const positionPart = (body: Record<string, Json>): Record<string, Json> => {
  const out: Record<string, Json> = {};
  for (const k of ['date', 'hour', 'parametric', 'coordinates'] as const) if (body[k] !== undefined) out[k] = body[k]!;
  return out;
};

/** The key of the position a body names, when the matter has it in view. */
export const keyIn = (matter: WorldMatter | undefined, body: Record<string, Json>): number | null => {
  if (!matter) return null;
  const want = typeof body.date === 'string'
    ? positionOf({ date: body.date, ...(typeof body.hour === 'number' ? { hour: body.hour } : {}) })
    : typeof body.parametric === 'number' && Array.isArray(body.coordinates)
      ? { parametric: body.parametric, coordinates: body.coordinates.filter((c): c is number => typeof c === 'number') }
      : null;
  if (!want) return null;
  for (const pos of matter.byKey.values()) if (samePosition(pos, want)) return pos.key;
  return null;
};

/**
 * A handover refused `held` is always the giver lacking the slots: the door
 * checks the giver first and names its root holder, and past that check it
 * refuses a taker by `capacity` or by the taker's own rules, never `held`
 * (with one seat, a giver that holds the slots leaves nobody else holding
 * them). The API's sentence then reads backwards, so it is said (rule 12),
 * whether the giver was named by id, by name, or as an event or series.
 */
export const describeRefusal = (record: CallRecord, refusal: Refusal): string => {
  const giverLacks = record.op === 'handover' && refusal.by === 'held' && refusal.conflictsWith.length === 1;
  return giverLacks ? `${refusal.detail} (the giver does not hold these)` : refusal.detail;
};

export interface FixContext {
  /** The World's matter, for "next free run". */
  readonly matter?: WorldMatter;
  /** Our workspace (`whoami`), to tell our own Worlds from other workspaces'. */
  readonly workspace?: string;
  /** Where the view is, for fix-its whose call named no position of its own. */
  readonly focus?: { readonly date?: string; readonly hour?: number };
  /** The World's asks (`listAsks`), for where an ask's slots are: an answer names only its token. */
  readonly asks?: Json;
  /** The World's relays (`listRelays`), to ask a copy's owner on one that exists. */
  readonly relays?: Json;
}

/** An ask of this World by token, from its `listAsks` record: where and which slots. */
const askOf = (asks: Json | undefined, token: Json | undefined): Record<string, Json> | null => {
  const list = objectOf(asks).asks;
  if (typeof token !== 'string' || !Array.isArray(list)) return null;
  const found = list.map(objectOf).find((a) => a.token === token);
  return found ?? null;
};

/** Relays with their legs' hands and lengths, from a `listRelays` record. */
const relaysOf = (relays: Json | undefined): { name: string; legs: { hand: string; length: number }[] }[] => {
  const list = objectOf(relays).relays;
  if (!Array.isArray(list)) return [];
  return list.map(objectOf).flatMap((r) => {
    if (typeof r.name !== 'string' || !Array.isArray(r.legs)) return [];
    const legs = r.legs.map(objectOf).flatMap((l) => (typeof l.hand === 'string' && typeof l.length === 'number' ? [{ hand: l.hand, length: l.length }] : []));
    return [{ name: r.name, legs }];
  });
};

/** A relay name for asking `authority` for a run: what the API accepts, and the same for the same ask. */
const relayNameFor = (authority: string, at: Record<string, Json>, slot: number): string => {
  const where = typeof at.date === 'string' ? `${at.date}${typeof at.hour === 'number' ? `-${at.hour}h` : ''}` : `${String(at.parametric)}-${Array.isArray(at.coordinates) ? at.coordinates.join('-') : ''}`;
  const world = authority.slice(authority.indexOf('/') + 1);
  const name = `ask-${world}-${where}-${slot}`.toLowerCase().replace(/[^a-z0-9._-]+/g, '-');
  return name.slice(0, 63);
};

/**
 * A call's arguments to send again: without its Idempotency-Key (sent again, the key would only
 * replay the refusal the server stored under it; the Client gives the retry its own), and with
 * its World named by the key of the persona that called, so the retry goes with that persona.
 */
export const againArgs = (record: CallRecord): Args => {
  const { 'Idempotency-Key': _key, ...args } = record.args as Record<string, Json>;
  return typeof args.w === 'string' ? { ...args, w: worldKey(record.persona, args.w) } : args;
};

/** The fix-it cards for a refused call; none when the answer was not a refusal. */
export const fixitsFor = (record: CallRecord, ctx: FixContext = {}): FixIt[] => {
  const refusal = refusalOf(record.answer?.body);
  const name = record.args.w;
  // Every refusal is a World's door speaking: its fix-its act on that World, as the persona that was refused.
  if (!refusal || record.answer?.status !== 409 || typeof name !== 'string') return [];
  const w = worldKey(record.persona, name);
  const r = record.args.r;
  const token = record.args.token;
  const body = bodyOf(record.args);
  const at = positionPart(body);
  const out: FixIt[] = [];
  const retry = (label: string): FixIt => ({ label, op: record.op, args: againArgs(record) });

  /** The first free run shaped like the one asked for, at the same position. */
  const nextRun = (wanted: Record<string, Json> = body): Json | null => {
    const asked = readSlots(wanted.slots);
    const key = keyIn(ctx.matter, wanted);
    const pos = key === null ? undefined : ctx.matter?.byKey.get(key);
    if (asked === null || !pos || !ctx.matter) return null;
    const run = nextFreeRun(pos.offered, asked, ctx.matter.leafSlotBits);
    return run === null || run === asked ? null : slotsOf(run);
  };

  /**
   * What a copy holds is its owner's to give: ask the owner for the first run
   * of what was wanted, on a relay here whose one leg is that hand and that
   * length, or by laying one (the relay hint of §4.4).
   */
  const askOwner = (authority: string): FixIt | null => {
    if (record.op !== 'claim' && record.op !== 'explainClaim' && record.op !== 'confirm') return null;
    const run = runsOf(readSlots(body.slots) ?? 0n)[0];
    if (!run) return null;
    const start = { ...at, slot: run.from };
    const isOwner = (hand: string): boolean =>
      hand === authority || (!hand.includes('/') && ctx.workspace !== undefined && `${ctx.workspace}/${hand}` === authority);
    const relay = relaysOf(ctx.relays).find((r) => r.legs.length === 1 && isOwner(r.legs[0]!.hand) && r.legs[0]!.length === run.length);
    if (relay) return { label: 'Ask its owner', op: 'relayAct', args: { w, r: relay.name, body: { act: 'ask', start } } };
    return { label: 'Ask its owner · lay a relay', op: 'layRelay', args: { w, body: { name: relayNameFor(authority, at, run.from), legs: [{ hand: authority, length: run.length }] } } };
  };

  switch (refusal.by) {
    case 'held':
    case 'capacity': {
      if (record.op === 'claim' || record.op === 'explainClaim' || record.op === 'confirm') {
        const who = body.who;
        const waiting = refusal.slots === 0n ? body.slots : slotsOf(refusal.slots);
        if (who !== undefined && waiting !== undefined) out.push({ label: 'Queue for it', op: 'queue', args: { w, body: { who, ...at, slots: waiting } } });
        const run = nextRun();
        // A new claim, and a new claim carries no rank: the API trusts a rank sent in a claim, so a remedy never offers one.
        const { rank: _rank, ...unranked } = body;
        if (run) out.push({ label: 'Next free run', op: 'claim', args: { w, body: { ...unranked, slots: run } } });
        if (refusal.by === 'capacity' && typeof refusal.fields.seats === 'number') {
          out.push({ label: 'Add a seat', op: 'setCapacity', args: { w, body: { seats: refusal.fields.seats + 1 } } });
        }
        // The door's own reasons, asked again without making anything (an explain already is one).
        if (record.op !== 'explainClaim') {
          const { soft: _soft, until: _until, ...claim } = body;
          out.push({ label: 'Explain', op: 'explainClaim', args: { w, body: claim } });
        }
      } else if (record.op === 'handover') {
        const from = typeof body.from === 'string' ? body.from : refusal.conflictsWith[0];
        if (from) out.push({ label: `See what ${from} holds`, op: 'holdings', args: { w, who: from, ...at } });
      } else if (record.op === 'relayAct') {
        const hand = refusal.conflictsWith[0];
        if (hand) out.push({ label: `Refresh ${hand}'s copy`, op: 'takeIn', args: { w, body: { hand } } });
        out.push(retry('Then ask again'));
        const start = objectOf(body.start);
        if (typeof r === 'string') {
          out.push({ label: 'Find windows', op: 'relayWindows', args: { w, r, ...positionPart(start), positions: 1 } });
        }
      } else if (record.op === 'answerAsk') {
        // An answer names only its token: where the ask stands and which slots it wants are in the World's asks.
        const ask = askOf(ctx.asks, token);
        const run = ask ? nextRun({ ...positionPart(ask), slots: ask.slots ?? null }) : null;
        if (run && typeof token === 'string') out.push({ label: 'Move to a free run', op: 'answerAsk', args: { w, token, body: { verdict: 'moveTo', slots: run } } });
        if (typeof token === 'string') out.push({ label: 'Say no', op: 'answerAsk', args: { w, token, body: { verdict: 'reject' } } });
      }
      break;
    }
    case 'foreign': {
      const authority = typeof refusal.fields.authority === 'string' ? refusal.fields.authority : null;
      if (!authority) break;
      const [ws, world] = authority.split('/');
      if (ctx.workspace !== undefined && ws === ctx.workspace && world && (record.op === 'claim' || record.op === 'release')) {
        out.push({ label: `${record.op === 'claim' ? 'Claim' : 'Release'} on ${world} itself`, op: record.op, args: { w: worldKey(record.persona, world), body: { ...body, who: authority } } });
      }
      const ask = askOwner(authority);
      if (ask) out.push(ask);
      out.push({ label: `Refresh ${authority}'s copy`, op: 'takeIn', args: { w, body: { hand: authority } } });
      break;
    }
    case 'open':
      if (typeof r === 'string') {
        out.push({ label: 'Withdraw what stands', op: 'relayAct', args: { w, r, body: { act: 'withdraw' } } });
        out.push({ label: 'Sync the answers', op: 'relayAct', args: { w, r, body: { act: 'sync' } } });
      }
      break;
    case 'untaken': {
      const party = typeof refusal.fields.party === 'string' ? refusal.fields.party : null;
      if (party) out.push({ label: `Take ${party} in`, op: 'takeIn', args: { w, body: { hand: party } } });
      out.push(retry('Then try again'));
      break;
    }
    case 'unasked': {
      const date = ctx.focus?.date;
      if (typeof r === 'string' && date) {
        const hour = ctx.focus?.hour;
        out.push({ label: 'Find windows', op: 'relayWindows', args: { w, r, date, ...(hour === undefined ? {} : { hour }), positions: 1 } });
      }
      break;
    }
    case 'unknown':
      out.push({ label: 'Read the asks again', op: 'listAsks', args: { w } });
      break;
    case 'stale': {
      const hand = refusal.conflictsWith[0];
      if (hand) out.push({ label: `Refresh ${hand}'s copy`, op: 'takeIn', args: { w, body: { hand } } });
      break;
    }
    default:
      break;
  }
  return out;
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n: number): string => String(n).padStart(2, '0');

/** "Tue 29 Sep" (with the year when it is not `thisYear`). */
export const dayWords = (date: string, thisYear: number): string => {
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return `${DAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}${d.getUTCFullYear() === thisYear ? '' : ` ${d.getUTCFullYear()}`}`;
};

/** A run of slots as clock times: "10:00", "10:00–12:00"; on a minute World (an hour given) "19:05–19:07". */
export const slotWords = (slots: bigint, hour?: number): string => {
  const runs = runsOf(slots);
  const at = (s: number): string => (hour === undefined ? `${pad(s)}:00` : `${pad(hour)}:${pad(s)}`);
  const end = (s: number): string => (hour === undefined ? `${pad(s)}:00` : `${pad(hour + Math.floor(s / 60))}:${pad(s % 60)}`);
  return runs.map((r) => (r.length === 1 ? at(r.from) : `${at(r.from)}–${end(r.from + r.length)}`)).join(', ');
};

const names = (xs: readonly string[]): string => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

/**
 * A refusal said the way a person would: the day and time, who has it, and the seats that decide it.
 * "Tue 29 Sep 10:00 is taken by alice: this World has 1 seat". Null where the door's words are already plain.
 */
export const plainRefusal = (record: CallRecord, refusal: Refusal, matter?: WorldMatter, thisYear = new Date().getUTCFullYear()): string | null => {
  if (refusal.by !== 'held' && refusal.by !== 'capacity') return null;
  if (!['claim', 'explainClaim', 'confirm', 'queue'].includes(record.op)) return null;
  const body = bodyOf(record.args);
  if (typeof body.date !== 'string') return null;
  const hour = typeof body.hour === 'number' ? body.hour : undefined;
  const slots = refusal.slots !== 0n ? refusal.slots : readSlots(body.slots) ?? 0n;
  if (slots === 0n) return null;
  const when = `${dayWords(body.date, thisYear)} ${slotWords(slots, hour)}`;
  const who = refusal.conflictsWith;
  const seats = typeof refusal.fields.seats === 'number' ? refusal.fields.seats : matter?.seats;
  const plural = slotsOf(slots).length > 1;
  if (refusal.by === 'capacity') {
    return `${when} ${plural ? 'are' : 'is'} full: ${seats === undefined ? 'every seat is' : `all ${seats} seat${seats === 1 ? ' is' : 's are'}`} taken${who.length ? ` (${names(who)})` : ''}`;
  }
  return `${when} ${plural ? 'are' : 'is'} taken${who.length ? ` by ${names(who)}` : ''}${seats === undefined ? '' : `: this World has ${seats} seat${seats === 1 ? '' : 's'}`}`;
};

/** The refusal beat for the conductor: shield, thread, sentence, fix-its. Null when the answer is not a refusal. */
export const refusalCue = (record: CallRecord, ctx: FixContext = {}): Extract<Cue, { kind: 'refusal' }> | null => {
  const refusal = refusalOf(record.answer?.body);
  if (!refusal || record.answer?.status !== 409) return null;
  // The World as the stores and the stage key it: a persona's by `B:mill`.
  const w = typeof record.args.w === 'string' ? worldKey(record.persona, record.args.w) : '';
  const body = bodyOf(record.args);
  const slots = refusal.slots !== 0n ? refusal.slots : readSlots(body.slots) ?? 0n;
  return {
    kind: 'refusal',
    id: record.id,
    world: w,
    key: keyIn(ctx.matter, body) ?? -1,
    slots,
    conflictsWith: refusal.conflictsWith,
    by: refusal.by,
    detail: describeRefusal(record, refusal),
    fixits: fixitsFor(record, ctx),
    ...(() => { const plain = plainRefusal(record, refusal, ctx.matter); return plain ? { plain } : {}; })(),
  };
};
